import React, { useEffect, useState } from 'react';
import { useAdminStore } from '@/store/adminStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Settings, Save, Megaphone, Trash2, Loader2, Palette, RotateCcw, Building2, Banknote, Percent, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { applyBrandColor } from '@/components/BrandProvider';
import { useI18n } from '@/components/I18nProvider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function AdminSettingsPage() {
  const { t } = useI18n();
  const { settings, announcements, fetchSettings, updateSettings, fetchAnnouncements, createAnnouncement, deleteAnnouncement } = useAdminStore();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [annForm, setAnnForm] = useState({ title: '', content: '', type: 'info', target: 'all' });

  useEffect(() => {
    fetchSettings();
    fetchAnnouncements();
  }, [fetchSettings, fetchAnnouncements]);

  useEffect(() => {
    if (settings && !form) {
      const init = { ...settings };
      if (!Array.isArray(init.bank_accounts) || init.bank_accounts.length === 0) {
        init.bank_accounts = (init.banka_iban || init.banka_hesap_adi || init.banka_adi)
          ? [{ account_holder: init.banka_hesap_adi || '', bank_name: init.banka_adi || '', iban: init.banka_iban || '' }]
          : [{ account_holder: '', bank_name: '', iban: '' }];
      }
      setForm(init);
    }
  }, [settings, form]);

  const updateBank = (i, field, val) => {
    const arr = [...(form.bank_accounts || [])];
    arr[i] = { ...arr[i], [field]: val };
    setForm({ ...form, bank_accounts: arr });
  };
  const addBank = () => setForm({ ...form, bank_accounts: [...(form.bank_accounts || []), { account_holder: '', bank_name: '', iban: '' }] });
  const removeBank = (i) => setForm({ ...form, bank_accounts: (form.bank_accounts || []).filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    setSaving(true);
    const res = await updateSettings(form);
    setSaving(false);
    if (res.success) {
      toast.success(t('settingsSaved'));      if (form.brand_color) {
        applyBrandColor(form.brand_color);
      }
    }
    else toast.error(res.error || 'Hata');
  };

  const handleCreateAnn = async () => {
    if (!annForm.title || !annForm.content) return toast.error('Başlık ve içerik zorunlu');
    const res = await createAnnouncement(annForm);
    if (res.success) {
      toast.success('Duyuru oluşturuldu');
      setAnnForm({ title: '', content: '', type: 'info', target: 'all' });
    } else toast.error(res.error || 'Hata');
  };

  const handleDeleteAnn = async (id) => {
    const res = await deleteAnnouncement(id);
    if (res.success) toast.success('Duyuru silindi');
    else toast.error(res.error || 'Hata');
  };

  if (!form) {
    return (
      <div className="space-y-3 sm:space-y-6 animate-fade-in" data-testid="admin-settings-page">
        <h1 className="font-heading text-3xl font-bold">Sistem Ayarları</h1>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6 animate-fade-in" data-testid="admin-settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Sistem Ayarları</h1>
        <p className="mt-1 text-muted-foreground">Genel sistem yapılandırması ve duyurular</p>
      </div>

      {/* Brand Color Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Palette className="h-5 w-5 text-primary" />Kurumsal Renk</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Uygulamanın ana rengini (primary) buradan değiştirebilirsiniz. Bu renk landing sayfası, butonlar ve tüm arayüzde uygulanır.</p>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="space-y-1.5">
              <Label>Renk Kodu</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.brand_color || '#1a224f'}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  className="w-12 h-10 rounded-lg border cursor-pointer"
                  data-testid="brand-color-picker"
                />
                <Input
                  value={form.brand_color || '#1a224f'}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  placeholder="#1a224f"
                  className="h-10 w-36 font-mono"
                  data-testid="brand-color-input"
                />
                {form.brand_color && form.brand_color !== '#1a224f' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 text-xs gap-1.5"
                    onClick={() => setForm({ ...form, brand_color: '#1a224f' })}
                    data-testid="reset-color-btn"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Sıfırla
                  </Button>
                )}
              </div>
            </div>
            <div className="flex-1">
              <Label>Önizleme</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="h-10 w-10 rounded-lg" style={{ backgroundColor: form.brand_color || '#1a224f' }} />
                <div className="h-10 px-4 rounded-lg flex items-center text-white text-sm font-medium" style={{ backgroundColor: form.brand_color || '#1a224f' }}>
                  Örnek Buton
                </div>
                <div className="h-10 px-4 rounded-lg flex items-center text-sm font-medium border-2" style={{ borderColor: form.brand_color || '#1a224f', color: form.brand_color || '#1a224f' }}>
                  Outline
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Firma Ayarlari */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" />Firma Ayarlari</CardTitle>
          <CardDescription>Siparis PDF'lerinde ve faturalarda gozukecek firma bilgileri</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Firma Unvani</Label>
              <Input
                value={form.firma_unvan || ''}
                onChange={(e) => setForm({ ...form, firma_unvan: e.target.value })}
                placeholder="Firma Unvanınız"
                data-testid="firma-unvan-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Firma Telefonu</Label>
              <Input
                value={form.firma_tel || ''}
                onChange={(e) => setForm({ ...form, firma_tel: e.target.value })}
                placeholder="0XXX XXX XX XX"
                data-testid="firma-tel-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">Firma Adresi</Label>
            <textarea
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.firma_adres || ''}
              onChange={(e) => setForm({ ...form, firma_adres: e.target.value })}
              placeholder="Adres satirlari (her satir ayri satir olarak PDF'de gozukur)"
              rows={2}
              data-testid="firma-adres-input"
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Banknote className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Banka Bilgileri (Havale/EFT)</span>
            </div>
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={addBank} data-testid="add-bank-btn">
              <Plus className="h-3.5 w-3.5" /> IBAN Ekle
            </Button>
          </div>
          <p className="text-xs text-muted-foreground -mt-1 mb-1">Birden fazla banka hesabı ekleyebilirsiniz. İlk hesap, sipariş PDF'lerinde birincil hesap olarak kullanılır.</p>

          <div className="space-y-3">
            {(form.bank_accounts || []).map((acc, i) => (
              <div key={i} className="rounded-lg border border-border p-3 space-y-3 relative bg-muted/30" data-testid={`bank-account-row-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Hesap #{i + 1}{i === 0 ? ' (Birincil)' : ''}</span>
                  {(form.bank_accounts || []).length > 1 && (
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeBank(i)} data-testid={`remove-bank-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-sm">Hesap Adi</Label>
                    <Input
                      value={acc.account_holder || ''}
                      onChange={(e) => updateBank(i, 'account_holder', e.target.value)}
                      placeholder="Firma Adınız"
                      data-testid={`bank-holder-${i}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Banka Adi</Label>
                    <Input
                      value={acc.bank_name || ''}
                      onChange={(e) => updateBank(i, 'bank_name', e.target.value)}
                      placeholder="Garanti BBVA"
                      data-testid={`bank-name-${i}`}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">IBAN</Label>
                  <Input
                    value={acc.iban || ''}
                    onChange={(e) => updateBank(i, 'iban', e.target.value)}
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                    className="font-mono"
                    data-testid={`bank-iban-${i}`}
                  />
                </div>
              </div>
            ))}
            {(form.bank_accounts || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">Henüz banka hesabı eklenmedi. "IBAN Ekle" ile başlayın.</p>
            )}
          </div>

          <Separator />

          <div className="flex items-center gap-2 mb-2">
            <Percent className="h-4 w-4 text-primary" />
            <span className="font-medium text-sm">KDV Ayari</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="space-y-1.5 w-32">
              <Label className="text-sm">KDV Orani (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="1"
                value={form.kdv_orani || 0}
                onChange={(e) => setForm({ ...form, kdv_orani: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                data-testid="kdv-orani-input"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-5">0 girilirse KDV uygulanmaz. Siparis PDF'lerinde Net + KDV + Toplam olarak gozukur.</p>
          </div>

          <Button onClick={handleSave} disabled={saving} className="mt-2" data-testid="save-firma-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Firma Ayarlarini Kaydet
          </Button>
        </CardContent>
      </Card>

      {/* System Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" />Genel Ayarlar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Bakim Modu</Label>
              <p className="text-sm text-muted-foreground">Aktif edilirse yeni kayitlar devre disi kalir</p>
            </div>
            <Switch
              checked={form.maintenance_mode}
              onCheckedChange={(v) => setForm({ ...form, maintenance_mode: v })}
              data-testid="maintenance-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Yeni Kayıt</Label>
              <p className="text-sm text-muted-foreground">Yeni firma kayıtlarına izin ver</p>
            </div>
            <Switch
              checked={form.allow_registration}
              onCheckedChange={(v) => setForm({ ...form, allow_registration: v })}
              data-testid="registration-switch"
            />
          </div>

          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Kaydet
          </Button>
        </CardContent>
      </Card>

      {/* Announcements */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" />Duyurular</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Başlık</Label>
              <Input value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} data-testid="ann-title" />
            </div>
            <div className="space-y-2">
              <Label>Hedef</Label>
              <Select value={annForm.target} onValueChange={(val) => setAnnForm({ ...annForm, target: val })} data-testid="ann-target">
                <SelectTrigger className="w-full h-10 bg-white" data-testid="ann-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Herkes</SelectItem>
                  <SelectItem value="company_admin">Firma Yöneticileri</SelectItem>
                  <SelectItem value="personnel">Personeller</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Icerik</Label>
            <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={annForm.content} onChange={(e) => setAnnForm({ ...annForm, content: e.target.value })} data-testid="ann-content" />
          </div>
          <Button onClick={handleCreateAnn} data-testid="create-ann-btn">Duyuru Oluştur</Button>

          <Separator />

          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.id} className="flex items-start justify-between rounded-lg border p-4" data-testid={`ann-${a.id}`}>
                <div>
                  <h4 className="font-medium">{a.title}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{a.content}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Hedef: {a.target} | {new Date(a.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDeleteAnn(a.id)} data-testid={`delete-ann-${a.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            {announcements.length === 0 && <p className="text-center text-sm text-muted-foreground">Henüz duyuru yok</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
